/**
 * Profile Selection Panel Component
 *
 * Provides dropdown selectors for Server Profiles, GPU Platforms, and Switch Models
 * using the comprehensive profile database.
 */

import { Section } from '../layout/Section';
import { ALL_SERVERS, getServersByVendor } from '../../data/servers';
import { ALL_SWITCHES } from '../../data/switches';
import { ALL_PLATFORMS } from '../../data/platforms';
import { useStore } from '../../store/store';

export function ProfileSelectionPanel() {
  const { configuration, updateGPUPlatform, updateNetwork, setGPUsPerNode } = useStore();

  // Handle HGX platform selection
  const applyHGXPlatform = () => {
    updateGPUPlatform({
      platformId: 'hgx-b200',
      platformName: 'NVIDIA HGX B200',
      family: 'Blackwell',
      gpusPerPlatform: 8,
      interconnect: 'NVLink',
      requiresLiquidCooling: true,
      tdpWatts: 1000,
      memoryGB: 192,
      memoryType: 'HBM3e',
    });
    setGPUsPerNode(8);
  };

  // Handle NVL 72 platform selection
  const applyNVL72Platform = () => {
    updateGPUPlatform({
      platformId: 'nvl-72-b200',
      platformName: 'NVIDIA NVL 72 B200',
      family: 'Blackwell',
      gpusPerPlatform: 72,
      interconnect: 'NVLink',
      requiresLiquidCooling: true,
      tdpWatts: 1000,
      memoryGB: 192,
      memoryType: 'HBM3e',
    });
    setGPUsPerNode(72);
  };

  // Handle East/West fabric type selection
  const applyEastWestFabricType = (fabricType: string) => {
    updateNetwork({
      eastWest: {
        ...configuration.network.eastWest,
        fabricType: fabricType as any,
      },
    });
  };

  return (
    <Section title="Profile Selection" collapsible isExpanded={true}>
      {/* GPU Platform Selector */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">GPU Platform</label>
        <select
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          onChange={(e) => {
            if (e.target.value === 'hgx') applyHGXPlatform();
            else if (e.target.value === 'nvl72') applyNVL72Platform();
          }}
          value={
            configuration.gpu.platformName?.includes('HGX')
              ? 'hgx'
              : configuration.gpu.platformName?.includes('NVL 72')
              ? 'nvl72'
              : ''
          }
        >
          <option value="" disabled>
            Select platform...
          </option>
          <option value="hgx">HGX Platform (8 GPUs per node)</option>
          <option value="nvl72">NVL 72 Platform (72 GPUs per node)</option>
        </select>
      </div>

      {/* East/West Fabric Type Selector */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          East/West Fabric Type
        </label>
        <select
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          value={configuration.network.eastWest.fabricType}
          onChange={(e) => applyEastWestFabricType(e.target.value)}
        >
          <option value="InfiniBand">InfiniBand</option>
          <option value="RoCE">RoCE (RDMA over Converged Ethernet)</option>
        </select>
      </div>

      {/* Platform Information (Read-only) */}
      <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
        <div className="font-semibold text-blue-900 mb-1">Current Configuration</div>
        <div className="text-gray-700">{configuration.gpu.platformName || 'Not selected'}</div>
        {configuration.gpu.platformName && (
          <div className="text-gray-600 text-[10px] mt-1">
            {configuration.scaling.gpusPerNode} GPUs per node • {configuration.network.eastWest.fabricType} fabric
          </div>
        )}
      </div>
    </Section>
  );
}
